import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Box, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Card, CardContent, Avatar } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import io from 'socket.io-client';
import CallIcon from '@mui/icons-material/Call';
import { Row, Col } from 'antd';
import CallEvents from './CallEvents';
import { useAuth } from '../context/AuthContext';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/dashboard`);
        setStats(response.data.stats);
        setRecentCalls(response.data.recentCalls || []);
        setLoading(false);
      } catch (err) {
        setError('Failed to load dashboard data');
        setLoading(false);
        console.error('Dashboard data fetch error:', err);
      }
    };

    fetchDashboardData();

    // Socket.io setup for realtime active calls
    const socket = io(process.env.REACT_APP_API_BASE_URL);
    socket.on('activeCalls', (calls) => {
      setActiveCalls(calls);
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  const chartData = {
    labels: ['Inbound', 'Outbound'],
    datasets: [
      {
        label: 'Call Distribution',
        data: stats ? [stats.inbound_calls, stats.outbound_calls] : [0, 0],
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgb(75, 192, 192)',
          'rgb(153, 102, 255)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Call Direction Distribution',
      },
    },
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'busy':
        return 'warning';
      case 'no-answer':
        return 'warning';
      case 'in-progress':
        return 'info';
      case 'received':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Show CallEvents only to admin users */}
      {currentUser?.isAdmin && (
        <Box sx={{ mb: 4 }}>
          <CallEvents />
        </Box>
      )}

      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}
          >
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Total Calls
            </Typography>
            <Typography component="p" variant="h4">
              {stats?.total_calls || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}
          >
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Inbound Calls
            </Typography>
            <Typography component="p" variant="h4">
              {stats?.inbound_calls || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}
          >
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Outbound Calls
            </Typography>
            <Typography component="p" variant="h4">
              {stats?.outbound_calls || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}
          >
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Total Users
            </Typography>
            <Typography component="p" variant="h4">
              {stats?.total_users || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{ p: 2 }}
          >
            <Bar data={chartData} options={chartOptions} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Unique Callers
            </Typography>
            <Typography component="p" variant="h4" sx={{ mb: 2 }}>
              {stats?.unique_callers || 0}
            </Typography>
            <Typography color="text.secondary" sx={{ flex: 1 }}>
              The number of distinct phone numbers that have placed calls in your system.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
              Recent Calls
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentCalls.map((call) => (
                    <TableRow key={call.id}>
                      <TableCell>{call.from_number}</TableCell>
                      <TableCell>{call.to_number}</TableCell>
                      <TableCell>
                        <Chip 
                          label={call.direction} 
                          color={call.direction === 'inbound' ? 'primary' : 'secondary'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={call.status} 
                          color={getStatusColor(call.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{new Date(call.created_at).toLocaleString()}</TableCell>
                      <TableCell>{call.end_time ? new Date(call.end_time).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        {call.price !== undefined && call.price !== null ? 
                          `¥${parseFloat(call.price).toFixed(0)}` : '-'}
                      </TableCell>
                      <TableCell>
                        {call.cost !== undefined && call.cost !== null ? 
                          `¥${parseFloat(call.cost).toFixed(0)}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentCalls.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No recent calls</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
